import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConsumerAuth } from '../context/ConsumerAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Shield, Lock, Car, Home, Building, Users } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginType, setLoginType] = useState('investor'); // 'investor' or 'consumer'
  const { login } = useAuth();
  const { login: consumerLogin } = useConsumerAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (loginType === 'consumer') {
        // BETH Finance Consumer Login
        await consumerLogin(email, password);
        toast.success('Welcome to BETH Finance!');
        navigate('/beth-finance/dashboard');
      } else {
        // Investor/Admin Login
        const result = await login(email, password);
        toast.success('Welcome back!');
        if (result.user?.is_admin) {
          navigate('/admin-dashboard');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Login failed';
      if (detail.includes('pending review')) {
        toast.error('Your application is still under review. You will be notified once approved.');
      } else if (detail.includes('rejected')) {
        toast.error(detail);
      } else if (detail.includes('apply for access')) {
        toast.error('No account found. Please submit an application first.');
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1628] flex flex-col" data-testid="login-page">
      {/* Header */}
      <header className="border-b border-[#1a2d4a] bg-[#0d1d35]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#b8860b] flex items-center justify-center">
              <span className="text-white font-bold text-xl">B</span>
            </div>
            <div>
              <span className="text-white font-bold text-lg tracking-wide">BETHEREUM</span>
              <p className="text-[#b8860b] text-xs tracking-widest">PRIVATE WEALTH MANAGEMENT</p>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/compliance" className="text-gray-400 hover:text-white text-sm">Compliance</Link>
            <Link to="/investor-guide" className="text-gray-400 hover:text-white text-sm">Investor Guide</Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Button 
            variant="ghost" 
            className="mb-6 text-gray-400 hover:text-white"
            onClick={() => navigate('/')}
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
          </Button>
          
          {/* Login Type Toggle */}
          <div className="mb-6 p-1 bg-[#1a2d4a] rounded-lg flex">
            <button
              onClick={() => setLoginType('investor')}
              className={`flex-1 py-3 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                loginType === 'investor' 
                  ? 'bg-[#b8860b] text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
              data-testid="investor-login-tab"
            >
              <Building className="w-4 h-4" />
              Investor Portal
            </button>
            <button
              onClick={() => setLoginType('consumer')}
              className={`flex-1 py-3 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                loginType === 'consumer' 
                  ? 'bg-[#E31837] text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
              data-testid="consumer-login-tab"
            >
              <Car className="w-4 h-4" />
              BETH Finance
            </button>
          </div>
          
          <Card className={`border ${loginType === 'consumer' ? 'bg-[#012169] border-[#01337d]' : 'bg-[#0d1d35] border-[#1a2d4a]'}`} data-testid="login-card">
            <CardHeader className="text-center pb-2">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                loginType === 'consumer' ? 'bg-[#E31837]/20' : 'bg-[#b8860b]/20'
              }`}>
                {loginType === 'consumer' ? (
                  <Car className="w-8 h-8 text-[#E31837]" />
                ) : (
                  <Lock className="w-8 h-8 text-[#b8860b]" />
                )}
              </div>
              <CardTitle className="text-2xl text-white">
                {loginType === 'consumer' ? 'BETH Finance Login' : 'Client Login'}
              </CardTitle>
              <CardDescription className="text-gray-400">
                {loginType === 'consumer' 
                  ? 'Access your credit enhancement account' 
                  : 'Access your private wealth account'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-300">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className={`border text-white placeholder:text-gray-500 ${
                      loginType === 'consumer' 
                        ? 'bg-[#01337d] border-[#014091]' 
                        : 'bg-[#1a2d4a] border-[#2a3d5a]'
                    }`}
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-gray-300">Password</Label>
                    <button 
                      type="button"
                      onClick={() => navigate('/forgot-password')} 
                      className={`text-xs hover:underline ${loginType === 'consumer' ? 'text-[#E31837]' : 'text-[#b8860b]'}`}
                      data-testid="forgot-password-link"
                    >
                      Forgot Password?
                    </button>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className={`border text-white placeholder:text-gray-500 ${
                      loginType === 'consumer' 
                        ? 'bg-[#01337d] border-[#014091]' 
                        : 'bg-[#1a2d4a] border-[#2a3d5a]'
                    }`}
                    data-testid="password-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className={`w-full font-medium ${
                    loginType === 'consumer' 
                      ? 'bg-[#E31837] hover:bg-[#c4142f] text-white' 
                      : 'bg-[#b8860b] hover:bg-[#9a7209] text-white'
                  }`}
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>
              </form>
              
              <div className="mt-6 text-center text-sm">
                <span className="text-gray-500">Need an account? </span>
                {loginType === 'consumer' ? (
                  <button 
                    onClick={() => navigate('/beth-finance/signup')} 
                    className="text-[#E31837] hover:underline font-medium"
                    data-testid="signup-link"
                  >
                    Apply for Credit Enhancement
                  </button>
                ) : (
                  <button 
                    onClick={() => navigate('/signup')} 
                    className="text-[#b8860b] hover:underline font-medium"
                    data-testid="signup-link"
                  >
                    Request Access
                  </button>
                )}
              </div>
              
              {/* Subscription Info - Only for Investor */}
              {loginType === 'investor' && (
                <div className="mt-4 pt-4 border-t border-[#2a3d5a]">
                  <p className="text-xs text-center text-gray-500">
                    New to Bethereum? <button onClick={() => navigate('/subscribe')} className="text-[#b8860b] hover:underline">View subscription plans</button>
                  </p>
                </div>
              )}
              
              {/* BETH Finance Info - Only for Consumer */}
              {loginType === 'consumer' && (
                <div className="mt-4 pt-4 border-t border-[#014091]">
                  <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
                    <div className="flex items-center gap-1">
                      <Car className="w-3 h-3" />
                      <span>Auto: $49</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Home className="w-3 h-3" />
                      <span>Mortgage: $199</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Security Notice */}
          <div className={`mt-6 p-4 rounded-lg border ${
            loginType === 'consumer' 
              ? 'bg-[#012169]/50 border-[#01337d]' 
              : 'bg-[#0d1d35]/50 border-[#1a2d4a]'
          }`}>
            <div className="flex items-start gap-3">
              <Shield className={`w-5 h-5 mt-0.5 ${loginType === 'consumer' ? 'text-[#E31837]' : 'text-[#b8860b]'}`} />
              <div>
                {loginType === 'consumer' ? (
                  <p className="text-sm text-gray-400">
                    <span className="text-white font-medium">BETH Finance:</span> Credit enhancement solutions to improve your loan approval odds with our network of trusted lenders.
                  </p>
                ) : (
                  <p className="text-sm text-gray-400">
                    <span className="text-white font-medium">Platform Access:</span> Subscription-based platform for accredited investors. 
                    BETH credits are non-transferable platform access credits.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-[#1a2d4a] bg-[#0d1d35] py-4">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-xs text-gray-500">
            © 2026 Bethereum Private Wealth Management. For accredited investors only.
            <Link to="/compliance" className="text-[#b8860b] hover:underline ml-2">Legal Disclosures</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Login;
